<template>
  <div ref="container" class="smart-captcha-invisible"></div>
</template>

<script>
// Thin invisible-mode wrapper around Yandex SmartCaptcha with the same
// interface the app already uses for vue-recaptcha: execute()/reset()
// methods, @verify/@expired/@error events.
// https://yandex.cloud/ru/docs/smartcaptcha/concepts/widget-methods

const SCRIPT_SRC = 'https://smartcaptcha.cloud.yandex.ru/captcha.js?render=onload&onload=__smartCaptchaOnLoad';
const ONLOAD_CALLBACK = '__smartCaptchaOnLoad';
let scriptPromise = null;

function loadSmartCaptchaScript() {
  if (window.smartCaptcha) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      scriptPromise = null; // allow a later retry to re-attempt the load
      reject(new Error('smartcaptcha script load timeout'));
    }, 15000);
    window[ONLOAD_CALLBACK] = () => {
      clearTimeout(timer);
      resolve();
    };
    const el = document.createElement('script');
    el.src = SCRIPT_SRC;
    el.defer = true;
    el.onerror = () => {
      clearTimeout(timer);
      scriptPromise = null;
      reject(new Error('smartcaptcha script load failed'));
    };
    document.head.appendChild(el);
  });
  return scriptPromise;
}

export default {
  name: 'SmartCaptchaInvisible',
  props: {
    sitekey: {
      type: String,
      required: true,
    },
  },
  emits: ['verify', 'expired', 'error', 'challenge-visible', 'challenge-hidden'],
  data() {
    return {
      widgetId: null,
      status: 'loading', // 'loading' | 'ready' | 'failed'
      pendingExecute: false,
      unmounted: false,
    };
  },
  mounted() {
    this.initWidget();
  },
  beforeUnmount() {
    this.unmounted = true;
    this.destroyWidget();
  },
  methods: {
    initWidget() {
      this.status = 'loading';
      loadSmartCaptchaScript()
        .then(() => {
          if (this.unmounted) return;
          this.destroyWidget(); // guard against a double render on re-init
          this.widgetId = window.smartCaptcha.render(this.$refs.container, {
            sitekey: this.sitekey,
            invisible: true,
            hl: 'ru',
            shieldPosition: 'bottom-right',
            callback: (token) => this.$emit('verify', token),
          });
          window.smartCaptcha.subscribe(this.widgetId, 'token-expired', () => this.$emit('expired'));
          window.smartCaptcha.subscribe(this.widgetId, 'network-error', () => this.$emit('error', 'network-error'));
          window.smartCaptcha.subscribe(this.widgetId, 'javascript-error', (error) => this.$emit('error', error));
          window.smartCaptcha.subscribe(this.widgetId, 'challenge-visible', () => this.$emit('challenge-visible'));
          window.smartCaptcha.subscribe(this.widgetId, 'challenge-hidden', () => this.$emit('challenge-hidden'));
          this.status = 'ready';
          if (this.pendingExecute) {
            this.pendingExecute = false;
            this.doExecute();
          }
        })
        .catch((error) => {
          if (this.unmounted) return;
          this.status = 'failed';
          this.pendingExecute = false;
          this.$emit('error', error);
        });
    },
    destroyWidget() {
      if (this.widgetId !== null && window.smartCaptcha) {
        try {
          window.smartCaptcha.destroy(this.widgetId);
        } catch (error) {
          // widget already gone — nothing to clean up
        }
      }
      this.widgetId = null;
    },
    doExecute() {
      window.smartCaptcha.execute(this.widgetId);
    },
    execute() {
      if (this.status === 'ready' && this.widgetId !== null && window.smartCaptcha) {
        this.doExecute();
      } else if (this.status === 'loading') {
        // Widget not ready yet (e.g. user clicked before the script finished
        // loading) — defer instead of emitting a false failure. The caller's
        // timeout still covers a script that never loads.
        this.pendingExecute = true;
      } else {
        // Previous load failed — retry the whole init, then execute.
        this.pendingExecute = true;
        this.initWidget();
      }
    },
    reset() {
      if (this.widgetId !== null && window.smartCaptcha) {
        window.smartCaptcha.reset(this.widgetId);
      }
    },
  },
};
</script>
